import { ChevronDown, ChevronRight, Search } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ModelPartnerInquiryDTO } from "@rt/models/all";
import { BASE_URL } from "@/Components/DetailPageView";
import { useThemeClass } from "@/Hooks/useThemeClass";
import clsx from "clsx";

function ContactCampaign() {
  const themeClass = useThemeClass();
  const faq = [
    "체험단마케팅",
    "바이럴마케팅",
    "퍼포먼스마케팅",
    "협찬마케팅",
    "인플루언서마케팅",
    "매장브랜딩",
    "오프라인마케팅",
    "해외마케팅",
    "플레이스마케팅",
    "인스타그램",
    "네이버",
    "유튜브",
    "틱톡",
    "클립",
    "음식점홍보",
    "병원홍보",
    "기타업종홍보",
    "컨설팅",
  ];

  const [formData, setFormData] = useState<ModelPartnerInquiryDTO>({
    business_name: "",
    industry: "",
    name: "",
    contact: "",
    question_type_list: [],
    url: "",
  });

  const [files, setFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [selectedFaq, setSelectedFaq] = useState<string[]>([]);

  const ALLOWED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/gif",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
    "application/msword", // doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
    "application/haansofthwp", // hwp
    "video/mp4",
  ];
  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE_MB = 10;

  const handleInputChange = (
    field: keyof ModelPartnerInquiryDTO,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);

    if (selected.length > MAX_FILES) {
      alert(`최대 ${MAX_FILES}개의 파일만 업로드할 수 있습니다.`);
      return;
    }

    const totalSize =
      selected.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    if (totalSize > MAX_TOTAL_SIZE_MB) {
      alert(`파일 전체 용량은 최대 ${MAX_TOTAL_SIZE_MB}MB 이하여야 합니다.`);
      return;
    }

    for (let file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`허용되지 않은 파일 형식입니다: ${file.name}`);
        return;
      }
    }

    setFileNames(selected.map((file) => file.name));
    setFiles(selected);
  };

  const toggleFaq = (item: string) => {
    const updatedList = selectedFaq.includes(item)
      ? selectedFaq.filter((el) => el !== item)
      : [...selectedFaq, item];

    setSelectedFaq(updatedList);
    setFormData((prev) => ({
      ...prev,
      question_type_list: updatedList,
    }));
  };

  const handleSubmit = async () => {
    if (
      !formData.business_name ||
      !formData.industry ||
      !formData.name ||
      !formData.contact ||
      formData.question_type_list.length === 0
    ) {
      alert("필수 항목을 모두 입력해 주세요.");
      return;
    }

    const submitUrl = `${BASE_URL}/partner-inquiry`;
    const form = new FormData();

    form.append("business_name", formData.business_name);
    form.append("industry", formData.industry);
    form.append("name", formData.name);
    form.append("contact", formData.contact);
    formData.question_type_list.forEach((item) =>
      form.append("question_type_list", item)
    );
    form.append("url", formData.url || "");

    files.forEach((file) => {
      form.append("files", file);
    });

    try {
      const res = await fetch(submitUrl, {
        method: "POST",
        body: form,
      });

      const result = await res.json();

      if (res.ok) {
        alert("문의가 성공적으로 접수되었습니다!");
        setFormData({
          business_name: "",
          industry: "",
          name: "",
          contact: "",
          question_type_list: [],
          url: "",
        });
        setSelectedFaq([]);
        setFiles([]);
        setFileNames([]);
        window.location.reload();
      } else {
        alert(result.meta?.message || "문의 접수 실패");
      }
    } catch (err) {
      console.error("Network Error:", err);
      alert("네트워크 오류가 발생했습니다.");
    }
  };

  return (
    <>
      <div className="px-3 mt-2">
        {/* Contact */}
        <div className="text-center justify-items-center mb-10">
          <h3 className={`${clsx(
            themeClass.text("text"))} text-[40px] font-black mb-2`}>
            Contact
          </h3>
          <p className="text-[#46B6D0] text-[12px] font-black tracking-[1em] mb-5">
            광고주문의
          </p>
          <p className={`${clsx(
            themeClass.text("text"))} text-[18px] font-bold whitespace-pre-line mb-3`}>
            함께, <br /> 마케팅 성공원칙을 공유하고 싶습니다.
          </p>
          <Link className="flex bg-[#8660B2] font-semibold text-white px-2 py-2 rounded-md" to={"/companyinfo"}>
            <button className="mr-2 ml-2">
              회사소개 및 클라이언트 보러가기
            </button>
            <ChevronRight />
          </Link>
        </div>

        {/* Input */}
        <div className=" justify-item-center items-center mb-5 w-full px-4">
          <div className="flex text-[14px] font-md justify-center gap-4 mb-4 w-full">
            <div className="w-full">
              <p className="mb-2">
                상호명 <span className="text-red-500">*</span>
              </p>
              <input
                type="text"
                className="rounded-full w-full border-gray-300 shadow-md"
                placeholder="업체명을 입력해 주세요"
                value={formData.business_name}
                onChange={(e) =>
                  handleInputChange("business_name", e.target.value)
                }
              />
            </div>
            <div className="w-full">
              <p className="mb-2">
                업종 <span className="text-red-500">*</span>
              </p>
              <input
                type="text"
                className="rounded-full w-full border-gray-300 shadow-md"
                placeholder="업종을 입력해 주세요"
                value={formData.industry}
                onChange={(e) => handleInputChange("industry", e.target.value)}
              />
            </div>
          </div>
          <div className="flex text-[14px] font-md justify-center gap-4">
            <div className="w-full">
              <p className="mb-2">
                성함 <span className="text-red-500">*</span>
              </p>
              <input
                type="text"
                className="rounded-full w-full border-gray-300 shadow-md"
                placeholder="대표님의 성함을 입력해 주세요"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>
            <div className="w-full">
              <p className="mb-2">
                연락처 <span className="text-red-500">*</span>
              </p>
              <input
                type="text"
                className="rounded-full w-full border-gray-300 shadow-md"
                placeholder="휴대폰번호를 입력해 주세요"
                value={formData.contact}
                onChange={(e) => handleInputChange("contact", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Faq */}
        <div className="mb-5 px-4">
          <p className="text-[14px] font-md mb-2">
            문의종류 <span className="text-red-500">*</span>
          </p>

          <div className="flex flex-wrap">
            {faq.map((el) => {
              const isSelected = selectedFaq.includes(el);

              return (
                <button
                  key={el}
                  type="button"
                  onClick={() => toggleFaq(el)}
                  className={`border rounded-full px-3 py-2 m-1 text-sm shadow-sm transition-colors
                ${
                    isSelected
                      ? " text-[#0F1E6C] border-[#0F1E6C]"
                      : "text-[#999999] border-gray-300 hover:border-[#0F1E6C]"
                  }
              `}
                >
                  {el}
                </button>
              );
            })}
          </div>
        </div>

        {/* Url */}
        <div className="mb-5 px-4">
          <p className="text-[14px] font-md mb-2">홈페이지 또는 플레이스 URL</p>
          <input
            type="text"
            className="rounded-full w-full border-gray-300 shadow-md"
            placeholder="홈페이지나 플레이스 URL을 입력해주세요"
            value={formData.url}
            onChange={(e) => handleInputChange("url", e.target.value)}
          />
        </div>

        {/* Input file */}
        <div className="mb-8 px-4">
          <p className="text-[14px] font-md mb-2">
            추가로 참고할 문서가 있을 경우 첨부해 주세요.
          </p>

          <div className="flex gap-2">
            <label htmlFor="file-upload" className="flex w-full cursor-pointer items-center gap-2">
              <input
                id="file-upload"
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.gif,.pptx,.doc,.docx,.hwp,.mp4,.pdf,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="w-full py-2.5 border border-gray-300 text-[#999999] rounded-full px-4 text-[14px] font-light bg-white truncate">
                {fileNames.length > 0
                  ? `${fileNames.length} file selected`
                  : "Select your file!"}
              </div>
              <div className="p-2 bg-[#8660B2] rounded-full">
                <Search className="text-white" />
              </div>
            </label>
          </div>

          {/* {fileNames.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
              {fileNames.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          )} */}
          <div className=" text-[8px] text-[#999999] mt-2">
            <p>
              첨부파일 합계 용량 10MB 이하 최대 5개 파일을 등록할 수 있습니다.
              <br />
              허용된 파일 형식 : png, jpg, gif, pptx, doc, docx, hwp, mp4, pdf, xlsx
            </p>
          </div>
        </div>

        {/* Button submit */}
        <div className="px-4">
            <button className={`${clsx(
              themeClass.bg("text"))} w-full rounded-full text-center py-5 text-white text-[18px] font-bold`} onClick={handleSubmit}>
              문의하기
            </button>
        </div>
      </div>
    </>
  );
}

export default ContactCampaign;
